import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft, CreditCard, MessageCircle, Minus, Plus, ShieldCheck, ShoppingBag, Truck } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { formatARS } from "@/lib/format";
import { waLink, productMsg } from "@/lib/whatsapp";
import { Reveal } from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";

export default function ProductPage() {
  const { id } = useParams();
  const { addItem } = useCart();
  const [p, setP] = useState(undefined);
  const [size, setSize] = useState(null);
  const [qty, setQty] = useState(1);
  const [imgIdx, setImgIdx] = useState(0);

  useEffect(() => {
    setP(undefined);
    setSize(null);
    setQty(1);
    setImgIdx(0);
    api.get(`/productos/${id}`).then((r) => setP(r.data)).catch(() => setP(null));
  }, [id]);

  if (p === undefined) {
    return (
      <main className="mx-auto max-w-7xl px-5 pt-32 sm:px-8 lg:pt-40">
        <div className="aspect-square w-full max-w-lg animate-pulse bg-panel" />
      </main>
    );
  }

  if (p === null) {
    return (
      <main className="mx-auto flex min-h-[60vh] max-w-7xl flex-col items-center justify-center gap-4 px-5 pt-32 text-center sm:px-8">
        <p className="font-display text-3xl font-black uppercase text-white">Producto no encontrado</p>
        <Link to="/tienda" data-testid="product-not-found-back-button" className="bg-volt px-6 py-3 font-display font-black uppercase tracking-widest text-black">
          Ver catálogo
        </Link>
      </main>
    );
  }

  const images = p.images?.length ? p.images : [null];
  const installments = (p.price / 3).toFixed(0);

  return (
    <main className="mx-auto max-w-7xl px-5 pb-24 pt-32 sm:px-8 lg:pt-40">
      <Link to="/tienda" data-testid="product-back-to-catalog-link" className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-zinc-500 transition-colors hover:text-volt">
        <ChevronLeft className="h-3.5 w-3.5" /> Volver al catálogo
      </Link>

      <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
        <Reveal>
          <div className="relative aspect-square overflow-hidden border border-white/10 bg-panel-2" data-testid="product-detail-image">
            {images[0] && (
              <img src={images[imgIdx]} alt={p.name} className="h-full w-full object-cover" />
            )}
            {p.badge && (
              <span className="absolute left-4 top-4 bg-volt px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-widest text-black">{p.badge}</span>
            )}
          </div>
          {images.length > 1 && (
            <div className="mt-3 flex gap-3">
              {images.map((im, i) => (
                <button
                  key={i}
                  data-testid={`product-thumb-${i}`}
                  onClick={() => setImgIdx(i)}
                  className={`h-20 w-20 overflow-hidden border ${i === imgIdx ? "border-volt" : "border-white/10"} transition-colors`}
                  aria-label={`Imagen ${i + 1}`}
                >
                  <img src={im} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </Reveal>

        <Reveal delay={0.1}>
          <p className="font-mono text-[11px] uppercase tracking-widest text-zinc-500">{p.brand || p.category}</p>
          <h1 className="mt-2 font-display text-3xl font-black uppercase leading-tight tracking-tight text-white sm:text-4xl" data-testid="product-detail-name">
            {p.name}
          </h1>
          <div className="mt-5 flex items-end gap-3">
            <p className="font-mono text-3xl font-bold text-volt" data-testid="product-detail-price">{formatARS(p.price)}</p>
            {p.original_price && (
              <p className="font-mono text-lg text-zinc-500 line-through">{formatARS(p.original_price)}</p>
            )}
          </div>
          <p className="mt-2 flex items-center gap-2 font-mono text-xs text-mp">
            <CreditCard className="h-4 w-4" /> 3 cuotas de {formatARS(Number(installments))} con Mercado Pago
          </p>

          {p.description && <p className="mt-6 text-sm leading-relaxed text-zinc-400">{p.description}</p>}

          {p.sizes?.length > 0 && (
            <div className="mt-7">
              <p className="font-mono text-[11px] uppercase tracking-widest text-zinc-400">Talle</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {p.sizes.map((s) => (
                  <button
                    key={s}
                    data-testid={`product-size-option-${s}`}
                    onClick={() => setSize(s)}
                    className={`min-w-12 border px-4 py-2.5 font-mono text-sm transition-colors ${
                      size === s ? "border-volt bg-volt text-black" : "border-white/15 text-zinc-300 hover:border-volt hover:text-volt"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
              {!size && <p className="mt-2 font-mono text-[11px] text-zinc-600">Elegí un talle para agregar al carrito</p>}
            </div>
          )}

          <div className="mt-7 flex items-center gap-4">
            <div className="flex items-center border border-white/15">
              <button data-testid="product-qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="flex h-11 w-11 items-center justify-center text-zinc-400 hover:text-volt" aria-label="Restar"><Minus className="h-4 w-4" /></button>
              <span className="w-10 text-center font-mono text-base text-white" data-testid="product-qty-value">{qty}</span>
              <button data-testid="product-qty-plus" onClick={() => setQty((q) => Math.min(10, q + 1))} className="flex h-11 w-11 items-center justify-center text-zinc-400 hover:text-volt" aria-label="Sumar"><Plus className="h-4 w-4" /></button>
            </div>
            <p className="font-mono text-xs uppercase tracking-widest text-zinc-500">
              {qty >= 2 ? "10% OFF en la 2da unidad" : "Sumá 2+ y llevá 10% OFF"}
            </p>
          </div>

          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <button
              data-testid="product-add-to-cart-button"
              onClick={() => addItem(p, size, qty)}
              disabled={p.sizes?.length > 0 && !size}
              className="inline-flex flex-1 items-center justify-center gap-3 bg-volt px-8 py-4 font-display text-base font-black uppercase tracking-widest text-black transition-colors hover:bg-volt-dim disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ShoppingBag className="h-5 w-5" /> Agregar al carrito
            </button>
            <a
              href={waLink(productMsg(p.name, size))}
              target="_blank"
              rel="noreferrer"
              data-testid="product-whatsapp-button"
              className="inline-flex flex-1 items-center justify-center gap-3 border border-wa px-8 py-4 font-display text-base font-black uppercase tracking-widest text-wa transition-colors hover:bg-wa hover:text-black"
            >
              <MessageCircle className="h-5 w-5" /> Consultar
            </a>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-3 border-t border-white/10 pt-6 sm:grid-cols-3">
            {[
              { icon: Truck, t: "Envío 24–48h" },
              { icon: CreditCard, t: "Mercado Pago" },
              { icon: ShieldCheck, t: "Compra protegida" },
            ].map((x) => (
              <div key={x.t} className="flex items-center gap-2.5 text-zinc-400">
                <x.icon className="h-4.5 w-4.5 text-volt" />
                <span className="font-mono text-[11px] uppercase tracking-widest">{x.t}</span>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </main>
  );
}
